import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { flag_01, plus } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useRef } from "react";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";

export type DefaultFlagCellProps = GridCustomCellProps & {
	readonly isDefault?: boolean;
	readonly onToggleDefault: (isDefault: boolean) => void | Promise<void>;
};

export const DefaultFlagCell = ({
	isDefault,
	onToggleDefault,
	...props
}: Readonly<DefaultFlagCellProps>) => {
	// The grid only swaps in a loading cell on the next commit, so a repeat
	// activation dispatched before then would otherwise fire a second mutation.
	const inFlightRef = useRef(false);

	const handleToggle = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			if (inFlightRef.current) return;
			inFlightRef.current = true;

			const release = () => {
				inFlightRef.current = false;
			};
			const result = onToggleDefault(!isDefault);
			if (result instanceof Promise) {
				void result.then(release, release);
			} else {
				release();
			}
		},
		[isDefault, onToggleDefault],
	);

	// Extract data-testid and other HTML attributes from props
	const { "data-testid": dataTestId, tdProps } = props as Record<
		string,
		unknown
	> & { tdProps?: React.HTMLAttributes<HTMLTableCellElement> };

	return (
		<GridTd data-testid={dataTestId as string | undefined} tdProps={tdProps}>
			<div className={"trax-grid-default-flag-cell"}>
				{isDefault ? (
					<Button
						onClick={handleToggle}
						svgIcon={flag_01}
						variant={"tertiary"}
					/>
				) : (
					<Button
						onClick={handleToggle}
						startIcon={<SvgIcon icon={plus} />}
						variant={"tertiary"}
					/>
				)}
			</div>
		</GridTd>
	);
};
